import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import categoryService from 'services/categoryService';
import { useStorefrontContext } from 'context/StorefrontContext';
import { Category } from 'types/category';
import { NavItem } from 'types/storefront';
import { ROUTES } from 'config/routes';
import HeaderSelectors from './HeaderSelectors';

interface CategoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

const defaultNav: NavItem[] = [
  { labelKey: 'nav.catalog', url: ROUTES.CATALOG },
  { labelKey: 'nav.promotions', url: ROUTES.PROMOTIONS },
  { labelKey: 'nav.news', url: ROUTES.NEWS },
  { labelKey: 'nav.brands', url: ROUTES.BRANDS },
];

const CategoryDrawer: React.FC<CategoryDrawerProps> = ({ open, onClose }) => {
  const { t } = useTranslation('common');
  const { navigation, features } = useStorefrontContext();
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    categoryService
      .getCategories()
      .then((data) => {
        if (!cancelled) setCategories(data);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const navItems = (navigation.header.length > 0 ? navigation.header : defaultNav).filter((item) => {
    if (item.url.includes('promociones') && !features.promotionsEnabled) return false;
    if (item.url.includes('marcas') && !features.brandsEnabled) return false;
    return true;
  });

  const labelFor = (item: NavItem) => item.label || (item.labelKey ? t(item.labelKey) : item.url);

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 300, sm: 340 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('nav.menu')}
          </Typography>
          <IconButton onClick={onClose} aria-label={t('nav.closeMenu')}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List dense disablePadding>
          {navItems.map((item) => (
            <ListItemButton
              key={item.url}
              component={RouterLink}
              to={item.url}
              onClick={onClose}
            >
              <ListItemText primary={labelFor(item)} />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <Typography variant="subtitle2" sx={{ px: 2, pt: 2, pb: 1, fontWeight: 700 }}>
          {t('catalog.categories')}
        </Typography>
        <Box sx={{ flexGrow: 1, overflow: 'auto', px: 1, pb: 2 }}>
          {categories.map((category) =>
            category.children?.length ? (
              <Accordion key={category.id} disableGutters elevation={0} square>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography
                    component={RouterLink}
                    to={`/categoria/${category.slug}`}
                    onClick={onClose}
                    sx={{ textDecoration: 'none', color: 'inherit', fontWeight: 600 }}
                  >
                    {category.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ py: 0 }}>
                  <List dense disablePadding>
                    {category.children.map((child) => (
                      <ListItemButton
                        key={child.id}
                        component={RouterLink}
                        to={`/categoria/${category.slug}/${child.slug}`}
                        onClick={onClose}
                      >
                        <ListItemText primary={child.name} />
                      </ListItemButton>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ) : (
              <ListItemButton
                key={category.id}
                component={RouterLink}
                to={`/categoria/${category.slug}`}
                onClick={onClose}
              >
                <ListItemText primary={category.name} />
              </ListItemButton>
            )
          )}
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            {t('nav.preferences')}
          </Typography>
          <HeaderSelectors />
        </Box>
      </Box>
    </Drawer>
  );
};

export default CategoryDrawer;
